import React, {useCallback, useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "@hooks/redux-hooks/hooks";
import {changeAzienda, changeAziendaNavLink, selectAziendaNavLink, selectIsBusy, selectToken, setupAzienda, setupAziendaMaster} from "@commons/core.slice";
import {useGetAziendaNavQuery, useGetAziendaQuery, useGetCurrentSelectionQuery} from "@commons/features/azienda/api";
import {usePagination} from "@components/dataTable/usePagination";
import AziendaNavigator from "./AziendaNavigator";
import AziendaSearchBar from "./AziendaSearchBar";
import ElencoAziende from "./AziendaElenco";
import {AziendaFilterSelection} from "@commons/utils/APIParams";
import {TokenRecord} from "@commons/mapping/TokenRecord";

export type AziendaSearchFormData = {
    codAzienda: string | null;
    filiale: string | null;
    denominazione: string | null;
    descrizione: string | null;
};

interface AziendaNavContainerProps {
    onNavSetIsBusy?: boolean,
    setShowDetail?: (state: boolean) => void,
    children?: React.ReactNode,
}

const AziendaNavContainer: React.FC<AziendaNavContainerProps> = (props) => {
    const isBusy: boolean = useAppSelector(selectIsBusy);
    const dispatch = useAppDispatch();
    //const params = useOutletContext<OutletContexParams>();

    //console.log('AziendaNavContainer: params', params);

    const token: TokenRecord = useAppSelector(selectToken);
    const [showData, setShowData] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);

    //const {refetch: refetchAzienda} = aziendaApi.endpoints.getAzienda.useQuerySubscription({idAzienda: token.idAzienda});
    const aziendaToken = useGetAziendaQuery({idAzienda: token.idAzienda}, {skip: !token.idAzienda});
    const aziendaMasterToken = useGetAziendaQuery({idAzienda: token.idAziendaMaster}, {skip: !token.idAziendaMaster});

    const link = useAppSelector(selectAziendaNavLink);

    const setLink = useCallback((link: string | null) => {
        dispatch(changeAziendaNavLink({link, setIsBusy: props.onNavSetIsBusy}));
    }, [dispatch, props.onNavSetIsBusy]);

    const {data, isLoading, isFetching} = useGetAziendaNavQuery({path: link!}, {skip: !link, refetchOnMountOrArgChange: true});
    //console.log('isLoading', isLoading);
    //console.log('isFetching', isFetching);

    function hasFilter(filter: Partial<AziendaFilterSelection>) {
        return filter && (filter.codAzienda === 0 || filter.codAzienda || filter.codFiliale || filter.denominazione || filter.unitaLocale);
    }

    const [filter, setFilter] = useState<Partial<AziendaFilterSelection>>({} as AziendaFilterSelection);
    const filterResult = useGetCurrentSelectionQuery(filter, {skip: !hasFilter(filter)});

    const {page, setPage, pageLength, setPageLength, getStart, search, setSearch, getSearchParams} = usePagination('');
    // https://dmitripavlutin.com/react-useeffect-explanation/
    // Dispatch in caso di variazione dell'azienda corrente!
    useEffect(() => {
        if (data) {
            dispatch(changeAzienda({idAzienda: data.data.idAzienda, idAziendaMaster: data.data.idAziendaMaster, URI: link}));
            setShowData(false); // Chiudo tabella risultati
        }
    }, [data, dispatch, link]);

    const selezionaAzienda = useCallback((idAzienda: number, idAziendaMaster: number) => {
        if (aziendaToken.data && aziendaToken.data.idAzienda === idAzienda) {
            dispatch(setupAzienda(aziendaToken.data)); // Ripristino azienda token
        }
        if (aziendaMasterToken.data && aziendaMasterToken.data.idAzienda === idAziendaMaster) {
            dispatch(setupAziendaMaster(aziendaMasterToken.data)); // Ripristino azienda token
        }
        setLink(`/azienda/${idAziendaMaster}/ul/${idAzienda}?mode=Navigator&view=Minimal`);
        setOpen(false);
        setShowData(false);
    }, [aziendaToken.data, aziendaMasterToken.data, setLink]);
    const selezionaAziendaToken = useCallback(() => selezionaAzienda(token.idAzienda, token.idAziendaMaster), [selezionaAzienda, token.idAzienda, token.idAziendaMaster]);

    const selAzByUser = (idAzienda: number, idAziendaMaster: number) => {
        selezionaAzienda(idAzienda, idAziendaMaster);
    }

// Impostazione iniziale della rotta
    useEffect(() => {
        if (link === null) // Solo prima volta o eventualmente se resettato al di fuori del componente (es.: pagina unita' locali)
            selezionaAziendaToken();
    }, [link, selezionaAziendaToken]);

// Impostazione della rotta al cambio di dominio
    useEffect(() => {
        selezionaAziendaToken();
    }, [token.idDominio]); // non mettere nelle dipendenze selezionaAziendaToken

// aggiorno query al variare della pagina selezionata o della lunghezza della pagina stessa!
    const newStart = getStart();
    const newSearch = getSearchParams()
    useEffect(() => {
        setFilter(filter => ({...filter, start: newStart, length: pageLength, search: newSearch}));
    }, [newStart, pageLength, newSearch]);

    // monitor risultati ricerca
    useEffect(() => {
        if (filterResult.data) {
            if (filterResult.data.recordsFiltered === 1) {
                selezionaAzienda(filterResult.data.data[0].idAzienda, filterResult.data.data[0].idAziendaMaster);
            }
        }
    }, [filterResult.data]);

    function searchAzienda(formData: AziendaSearchFormData) {
        if (formData) {
            dispatch(setupAzienda(null)); // reset al fine di evitare caricamenti inutili al variare dell'azienda (vedi es. GestionePlusPage)
            dispatch(setupAziendaMaster(null)); // reset al fine di evitare caricamenti inutili al variare dell'azienda (vedi es. GestionePlusPage)

            setShowData(true);
            setPage(1);

            let newFilter: Partial<AziendaFilterSelection> = {
                start: getStart(),
                length: pageLength,
                mode: "DataTables",
                view: "Grid",
                search: getSearchParams()
            };
            newFilter.codAzienda = formData.codAzienda ? parseInt(formData.codAzienda) : 0;
            if (formData.filiale)
                newFilter.codFiliale = formData.filiale;
            if (formData.denominazione)
                newFilter.denominazione = formData.denominazione;
            if (formData.descrizione)
                newFilter.unitaLocale = formData.descrizione;
            setFilter(newFilter);
        }
    }

    function resetQuery() {
        if (aziendaToken && aziendaToken.data)
            dispatch(setupAzienda(aziendaToken.data)); // Ripristino azienda token
        if (aziendaMasterToken && aziendaMasterToken.data)
            dispatch(setupAziendaMaster(aziendaMasterToken.data)); // Ripristino azienda token
        setShowData(false);
    }

    return (<>
        {token && <>
            <AziendaNavigator setLink={setLink} isLoading={isLoading || isFetching || isBusy} aziendaNavData={data}/>


            {aziendaToken.data && <AziendaSearchBar azienda={aziendaToken.data}
                                                    onSubmit={searchAzienda}
                                                    onReset={resetQuery}
                                                    open={open}
                                                    setOpen={setOpen}
                                                    selezionaAzienda={selezionaAzienda}/>}

            {showData && <ElencoAziende dataTables={filterResult.data}
                                        currentPage={page} setPage={setPage}
                                        currentPageLength={pageLength} setPageLength={setPageLength}
                                        currentSearch={search} setSearch={setSearch}
                                        selezionaAzienda={selAzByUser}/>}
            {!showData && props.children}
        </>}
    </>)
}

export default AziendaNavContainer;

//https://github.com/haldarmahesh/use-mobile-detect-hook
//https://www.npmjs.com/package/react-device-detect